import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase, BlogPost } from '@/lib/supabase';
import { SeoHead } from '@/components/seo/SeoHead';
import { Loader2, Calendar, Clock, ChevronRight, Flame, Bookmark, Facebook, Twitter, Link as LinkIcon, List, User, CheckCircle2, ArrowRight } from 'lucide-react';
import { BlogCard } from '@/components/blog/BlogCard';
import { InPostCTA } from '@/components/blog/InPostCTA';

export function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  
  // Estados para el contenido dividido
  const [contentPart1, setContentPart1] = useState('');
  const [contentPart2, setContentPart2] = useState('');
  const [tableOfContents, setTableOfContents] = useState<{id: string, text: string}[]>([]);

  useEffect(() => {
    async function fetchPostAndRelated() {
      if (!slug) return;
      setLoading(true);
      
      const { data: currentPost, error } = await supabase
        .from('blog_posts')
        .select('*, categories:blog_categories(id, name, slug)')
        .eq('slug', slug)
        .eq('status', 'published')
        .single();

      if (currentPost) {
        setPost(currentPost);
        processContent(currentPost.content);
        supabase.rpc('increment_blog_view', { post_id: currentPost.id });

        let relatedQuery = supabase
            .from('blog_posts')
            .select('*, categories:blog_categories(name, slug)')
            .eq('status', 'published')
            .neq('id', currentPost.id)
            .limit(3);

        if (currentPost.category_id) {
            const { data: byCategory } = await relatedQuery.eq('category_id', currentPost.category_id);
            if (byCategory && byCategory.length >= 1) {
                setRelatedPosts(byCategory);
            } else {
                const { data: latest } = await supabase
                    .from('blog_posts')
                    .select('*, categories:blog_categories(name, slug)')
                    .eq('status', 'published')
                    .neq('id', currentPost.id)
                    .limit(3);
                setRelatedPosts(latest || []);
            }
        } else {
             const { data: latest } = await relatedQuery;
             setRelatedPosts(latest || []);
        }
      }
      setLoading(false);
    }
    fetchPostAndRelated();
  }, [slug]);

  const processContent = (html: string) => {
    if (!html) return;
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // 1. Generar TOC
    const h2Elements = doc.querySelectorAll('h2');
    const toc: {id: string, text: string}[] = [];

    // 2. Envolver tablas
    const tables = doc.querySelectorAll('table');
    tables.forEach(table => {
        const wrapper = doc.createElement('div');
        wrapper.className = 'table-wrapper';
        table.parentNode?.insertBefore(wrapper, table);
        wrapper.appendChild(table);
    });

    h2Elements.forEach((h2, index) => {
        const text = h2.textContent?.trim() || `Sección ${index + 1}`;
        const id = text
            .toLowerCase()
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
        
        h2.id = id;
        toc.push({ id, text });
    });

    setTableOfContents(toc);

    // 3. LÓGICA DE DIVISIÓN (SPLIT)
    
    // Buscar el marcador manual (ahora es un div con clase blog-cta-placeholder)
    const ctaPlaceholder = doc.querySelector('.blog-cta-placeholder');
    
    if (ctaPlaceholder) {
        // --- MODO MANUAL ROBUSTO ---
        // Encontrar el ancestro de nivel superior (hijo directo de body)
        // Esto es necesario porque a veces Quill envuelve el placeholder en un <p>
        let splitNode = ctaPlaceholder;
        while (splitNode.parentNode && splitNode.parentNode !== doc.body) {
            splitNode = splitNode.parentNode as Element;
        }

        const children = Array.from(doc.body.children);
        const div1 = document.createElement('div');
        const div2 = document.createElement('div');
        let foundCTA = false;
        
        children.forEach(child => {
            if (child === splitNode) {
                foundCTA = true;
                return; // No incluimos el marcador visual en el HTML final
            }
            
            if (!foundCTA) {
                div1.appendChild(child.cloneNode(true));
            } else {
                div2.appendChild(child.cloneNode(true));
            }
        });

        setContentPart1(div1.innerHTML);
        setContentPart2(div2.innerHTML);

    } else {
        // --- MODO AUTOMÁTICO (FALLBACK) ---
        const children = Array.from(doc.body.children);
        
        if (children.length > 4) {
            const middleIndex = Math.floor(children.length / 2);
            
            const div1 = document.createElement('div');
            const div2 = document.createElement('div');

            children.forEach((child, index) => {
                if (index < middleIndex) {
                    div1.appendChild(child.cloneNode(true));
                } else {
                    div2.appendChild(child.cloneNode(true));
                }
            });

            setContentPart1(div1.innerHTML);
            setContentPart2(div2.innerHTML);
        } else {
            setContentPart1(doc.body.innerHTML);
            setContentPart2('');
        }
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Componente Auxiliar para el Índice
  const TableOfContents = ({ items, className = '', mobile = false }: { items: {id: string, text: string}[], className?: string, mobile?: boolean }) => {
    if (items.length === 0) return null;
    return (
        <div className={`bg-slate-50 p-6 rounded-xl border border-slate-200 ${className}`}>
            <h4 className={`font-bold text-slate-900 mb-4 flex items-center gap-2 uppercase tracking-wider text-xs border-b border-slate-200 pb-2 ${!mobile ? 'sticky top-0 bg-slate-50 z-10' : ''}`}>
                <List className="w-4 h-4 text-slate-500" />
                Contenido del artículo
            </h4>
            <nav className="flex flex-col gap-2">
                {items.map((item, idx) => (
                    <a 
                        key={item.id} 
                        href={`#${item.id}`}
                        className="text-sm text-slate-600 hover:text-blue-700 hover:bg-white px-3 py-2.5 rounded-md transition-all flex items-start gap-3 group w-full"
                        onClick={(e) => {
                            e.preventDefault();
                            document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
                        }}
                    >
                        <span className="text-slate-300 font-mono text-xs mt-0.5 group-hover:text-blue-400 shrink-0 font-bold">
                            {(idx + 1).toString().padStart(2, '0')}
                        </span>
                        <span className="leading-snug break-words flex-1 min-w-0">
                            {item.text}
                        </span>
                    </a>
                ))}
            </nav>
        </div>
    );
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-blue-600" /></div>;
  if (!post) return <div className="min-h-screen flex flex-col items-center justify-center text-slate-500"><h1 className="text-2xl font-bold mb-4">Artículo no encontrado</h1><Link to="/blog" className="text-blue-600 hover:underline">Volver al blog</Link></div>;

  const readTime = Math.ceil(post.content.split(/\s+/).length / 200);

  return (
    <div className="bg-white min-h-screen pb-20">
      <SeoHead 
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt}
        image={post.image_url}
        type="article"
        publishedTime={post.published_at}
      />

      {/* --- HEADER MEJORADO E INTELIGENTE --- */}
      {post.image_url ? (
        <div className="relative w-full bg-slate-900">
            {/* 
                Contenedor Adaptativo:
                - Mobile: 'aspect-video' (16:9) asegura que los banners horizontales se vean completos.
                - Desktop: 'md:h-[65vh]' mantiene el impacto visual de un hero section.
            */}
            <div className="relative w-full aspect-video md:h-[65vh] md:aspect-auto overflow-hidden group">
                
                {/* 1. Fondo Ambiente (Blurred): Rellena los huecos si la imagen no encaja perfectamente */}
                <div 
                    className="absolute inset-0 bg-cover bg-center opacity-50 blur-2xl scale-110 transition-opacity duration-700"
                    style={{ backgroundImage: `url(${post.image_url})` }}
                ></div>

                {/* 2. Gradiente de Legibilidad: Más fuerte abajo para asegurar que el texto se lea */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent z-10"></div>
                
                {/* 3. Imagen Principal "Inteligente":
                    - object-contain: La clave. Asegura que NUNCA se recorte la imagen, mostrando todo el texto/contenido.
                    - El fondo blurred se encarga de rellenar el resto de la pantalla.
                */}
                <img 
                    src={post.image_url} 
                    alt={post.title} 
                    className="relative z-0 w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.02]" 
                />
                
                {/* 4. Contenido Superpuesto */}
                <div className="absolute bottom-0 left-0 w-full z-20 p-4 md:p-12 flex flex-col justify-end h-full pointer-events-none">
                    <div className="max-w-7xl mx-auto px-4 w-full pointer-events-auto">
                        <div className="animate-in slide-in-from-bottom-4 duration-700">
                            {post.categories && (
                                <Link 
                                    to={`/blog?category=${post.categories.slug}`} 
                                    className="inline-flex items-center gap-1 bg-blue-600/90 backdrop-blur-md text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4 hover:bg-blue-600 transition-colors shadow-lg"
                                >
                                    {post.categories.name}
                                </Link>
                            )}
                            
                            <h1 className="text-2xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight drop-shadow-xl max-w-5xl tracking-tight text-balance">
                                {post.title}
                            </h1>
                            
                            <div className="flex flex-wrap items-center gap-6 text-slate-200 text-sm font-medium border-t border-white/20 pt-6 max-w-2xl">
                                <span className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/20">
                                        <User className="w-4 h-4 text-white" />
                                    </div>
                                    Equipo Editorial
                                </span>
                                <span className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4 opacity-70" />
                                    {new Date(post.published_at || post.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                                </span>
                                <span className="flex items-center gap-2">
                                    <Clock className="w-4 h-4 opacity-70" />
                                    {readTime} min lectura
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      ) : (
        <div className="bg-slate-900 pt-32 pb-16 px-4">
            <div className="max-w-4xl mx-auto text-center">
                <h1 className="text-4xl md:text-6xl font-black text-white mb-8 leading-tight">
                    {post.title}
                </h1>
            </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-12 overflow-x-auto whitespace-nowrap pb-2 border-b border-slate-100">
            <Link to="/" className="hover:text-blue-600 transition-colors">Inicio</Link>
            <ChevronRight className="w-4 h-4 text-slate-300" />
            <Link to="/blog" className="hover:text-blue-600 transition-colors">Magazine</Link>
            {post.categories && (
                <>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                    <Link to={`/blog?category=${post.categories.slug}`} className="hover:text-blue-600 transition-colors">
                        {post.categories.name}
                    </Link>
                </>
            )}
            <ChevronRight className="w-4 h-4 text-slate-300" />
            <span className="text-slate-900 font-medium truncate max-w-[200px]">{post.title}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* --- ARTÍCULO (8/12) --- */}
            <article className="lg:col-span-8 min-w-0">
                <div className="text-xl md:text-2xl text-slate-600 leading-relaxed font-serif mb-10 pl-6 border-l-4 border-slate-900 italic">
                    {post.excerpt}
                </div>

                {/* ÍNDICE DE CONTENIDOS (TOC) - VERSIÓN MÓVIL */}
                {/* Se muestra solo en pantallas pequeñas (lg:hidden) */}
                <div className="lg:hidden mb-10">
                    <TableOfContents items={tableOfContents} mobile={true} />
                </div>

                {/* PARTE 1 DEL CONTENIDO */}
                <div 
                    className="magazine-content prose prose-lg prose-slate max-w-none break-words text-justify hyphens-auto
                    prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-slate-900
                    prose-p:text-slate-700 prose-p:leading-8 prose-p:mb-6
                    prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
                    prose-img:rounded-xl prose-img:shadow-lg prose-img:my-8"
                    dangerouslySetInnerHTML={{ __html: contentPart1 }} 
                />

                {/* CTA AUTOMÁTICO O MANUAL (SIEMPRE SE RENDERIZA ENTRE PART1 Y PART2) */}
                {(contentPart2 || (!contentPart2 && tableOfContents.length > 0)) && <InPostCTA />}

                {/* PARTE 2 DEL CONTENIDO */}
                {contentPart2 && (
                    <div 
                        className="magazine-content prose prose-lg prose-slate max-w-none break-words text-justify hyphens-auto
                        prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-slate-900
                        prose-p:text-slate-700 prose-p:leading-8 prose-p:mb-6
                        prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
                        prose-img:rounded-xl prose-img:shadow-lg prose-img:my-8"
                        dangerouslySetInnerHTML={{ __html: contentPart2 }} 
                    />
                )}

                <div className="mt-16 pt-8 border-t border-slate-200">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                        <div className="flex items-center gap-2">
                             <div className="bg-slate-100 p-2 rounded-full">
                                <Bookmark className="w-5 h-5 text-slate-500" />
                             </div>
                             <span className="font-bold text-slate-700 text-sm uppercase tracking-wide">Comparte este artículo:</span>
                        </div>
                        <div className="flex gap-3">
                            <button onClick={handleShare} className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 font-medium transition-colors">
                                {copied ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <LinkIcon className="w-4 h-4" />}
                                {copied ? 'Copiado' : 'Copiar enlace'}
                            </button>
                            <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-full bg-[#1DA1F2]/10 text-[#1DA1F2] hover:bg-[#1DA1F2] hover:text-white transition-colors">
                                <Twitter className="w-5 h-5" />
                            </a>
                            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-full bg-[#4267B2]/10 text-[#4267B2] hover:bg-[#4267B2] hover:text-white transition-colors">
                                <Facebook className="w-5 h-5" />
                            </a>
                        </div>
                    </div>
                </div>
            </article>

            {/* --- SIDEBAR (4/12) --- */}
            <aside className="lg:col-span-4 space-y-8 sticky top-24">
                
                {/* ÍNDICE DE CONTENIDOS (TOC) - VERSIÓN DESKTOP */}
                {/* Se muestra solo en pantallas grandes (hidden lg:block) */}
                <div className="hidden lg:block">
                    <TableOfContents items={tableOfContents} className="max-h-[70vh] overflow-y-auto custom-scrollbar" />
                </div>

                <div className="bg-slate-900 text-white p-8 rounded-2xl shadow-2xl overflow-hidden group relative text-center">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-blue-600 rounded-full blur-[60px] opacity-30 group-hover:opacity-40 transition-opacity"></div>
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-600 rounded-full blur-[50px] opacity-20 group-hover:opacity-30 transition-opacity"></div>
                    
                    <div className="relative z-10">
                        <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                            <Flame className="w-6 h-6 text-yellow-400" />
                        </div>
                        <h3 className="text-xl font-bold mb-3">¿Tienes un trastero vacío?</h3>
                        <p className="text-slate-300 mb-8 text-sm leading-relaxed">
                            No pierdas dinero. Publícalo gratis en nuestra plataforma y encuentra inquilino en tiempo récord.
                        </p>
                        <Link 
                            to="/publish" 
                            className="block w-full bg-white text-slate-900 font-bold py-4 rounded-xl hover:bg-blue-50 transition-all shadow-lg transform hover:-translate-y-1"
                        >
                            Publicar Gratis
                        </Link>
                    </div>
                </div>
            </aside>
        </div>

        {relatedPosts.length > 0 && (
            <div className="mt-24 pt-16 border-t border-slate-200">
                <div className="flex items-center justify-between mb-8">
                    <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
                        También te puede interesar
                    </h2>
                    <Link to="/blog" className="hidden md:flex items-center gap-1 text-blue-600 font-bold hover:underline">
                        Ver todo el magazine <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {relatedPosts.map(relPost => (
                        <BlogCard key={relPost.id} post={relPost} />
                    ))}
                </div>
            </div>
        )}
      </div>
    </div>
  );
}
